import {
  buildUserDataObject,
  buildRrogatObject,
  buildPlateObject,
  buildCelObject,
  buildValueSasiaObject,
  buildErrorObject,
} from './datamodule.js';

import { showModalError } from './htmlSnipetBuilder.js';
import { DOMAIN, API } from './constants.js';

const containerModal = document.querySelector('.container-modal');
const baseUrl = `${DOMAIN.BASE_URL}/`;
const endpointPath = API.BASE_ENDPOINT_PATH;
let cognitoJwtToken = null;
const error = (msg, details = null) => {
  console.error('Error:', msg, details);
  let errorMessage = msg;

  if (typeof msg === 'object' && msg.message) {
    errorMessage = msg.message;
  }

  if (details && details.type === 'AUTH_ERROR') {
    // Force re-login on auth errors
    window.location.reload();
    return;
  }

  showModalError(containerModal, errorMessage);
};

const setCognitoJwtToken = function (value) {
  cognitoJwtToken = value;
};

const hideSpinner = () => {
  //Re-enable click on html element.
  document.documentElement.classList.remove('gjcdisableclick');
  const spinner = document.querySelector('.gjcloader');
  spinner.hidden = true;
};

const showSpinner = () => {
  //Disable click on html element.
  document.documentElement.classList.add('gjcdisableclick');
  const spinner = document.querySelector('.gjcloader');
  spinner.hidden = false;
};

const callGjcApi = async function (endpoint, dataObject) {
  let responseArray = [];
  showSpinner();
  try {
    if (!endpoint.startsWith('http')) {
      throw new Error('Invalid API endpoint URL');
    }

    // If token is expired, the fetch will fail with 401/403
    let res = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    // If we get an auth error, try refreshing the token
    if (res.status === 401 || res.status === 403) {
      console.log('Token expired, attempting refresh before retry...');
      const newToken = await (window.refreshSession
        ? window.refreshSession()
        : null); // assuming refreshSession is global
      if (newToken) {
        // Update the endpoint with new token
        endpoint = endpoint.replace(
          /cognito_token=[^&]+/,
          `cognito_token=${newToken}`,
        );
        // Retry the request with new token
        res = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
      } else {
        // Could not refresh - treat as auth error
        error('Authentication failed - token expired');
        hideSpinner();
        return [];
      }
    }
    const data = await res.text();

    // Log raw response for debugging
    console.log('Raw API Response:', data);
    try {
      console.log('Response status:', res.status);
      // log headers as object
      const headersObj = Object.fromEntries(res.headers.entries());
      console.log('Response headers:', headersObj);

      // --- PHP CONSOLE LOGGING ---
      console.groupCollapsed('PHP Server Logs');
      for (const [key, value] of res.headers.entries()) {
        if (key.startsWith('x-php-console-log-')) {
          try {
            const decoded = JSON.parse(atob(value));
            const logType = decoded.type || 'log';
            console[logType]
              ? console[logType](decoded.data)
              : console.log(decoded.data);
          } catch (e) {
            console.error('Failed to parse PHP log header:', e);
          }
        }
      }
      console.groupEnd();
      // --- END PHP CONSOLE LOGGING ---
    } catch (e) {
      console.warn('Could not read response status/headers', e);
    }

    if (!data) {
      console.error('Empty response from server');
      throw new Error('Empty response from server');
    }

    // crude check for HTML (common with 500 pages)
    if (data.trim().startsWith('<')) {
      console.error('Server returned HTML error page');
      console.log('Raw server HTML:', data);
      throw new Error('Server error (HTML) returned');
    }

    // remove accidental leading 'Array' artifact and attempt parse
    const correctedData = data.replace(/^Array\s*/i, '');

    let parsedData;
    try {
      parsedData = JSON.parse(correctedData);
    } catch (e) {
      console.error('JSON Parse Error:', e);
      console.log('Failed to parse server response:', correctedData);
      throw new Error('Invalid response format from server');
    }

    const errorObject = buildErrorObject(parsedData);
    if (errorObject.statusCode || errorObject.body || errorObject.message) {
      error(
        `${errorObject.statusCode}, ${errorObject.body}, ${errorObject.message}`,
      );
      throw new Error(
        `${errorObject.statusCode}, ${errorObject.body}, ${errorObject.message}`,
      );
    }
    if (!res.ok) {
      error(`${data.message}`);
      throw new Error(`${data.message}`);
    }
    const userDataArray = parsedData.users;

    responseArray = userDataArray
      ? userDataArray.map(obj => {
          return dataObject(obj);
        })
      : dataObject(parsedData);
  } catch (err) {
    error(err);
  }
  hideSpinner();
  return responseArray;
};

const queryByFirstLastName = async function (
  fname,
  lname,
  token = cognitoJwtToken,
) {
  if (!fname || !lname || !token) {
    error(
      `Invalid parameters: fname: ${fname}, lname: ${lname}, token: ${token}`,
    );
    return null;
  }
  // Ensure token is resolved if it's a Promise
  const resolvedToken = token instanceof Promise ? await token : token;
  const url = `${baseUrl}${endpointPath}?gjc_first_name=${fname}&gjc_last_name=${lname}&cognito_token=${resolvedToken}`;
  return callGjcApi(url, buildUserDataObject);
};

const queryGjcById = async function (nid, token = cognitoJwtToken) {
  if (!nid || !token) {
    error(`Invalid parameters: fname: ${nid}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${endpointPath}?gjc_id=${nid}&cognito_token=${token}`;
  return callGjcApi(url, buildUserDataObject);
};

const queryGjcByFamilyId = async function (nid, token = cognitoJwtToken) {
  if (!nid || !token) {
    error(`Invalid parameters: fname: ${nid}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${endpointPath}?gjc_family_id=${nid}&cognito_token=${token}`;
  return callGjcApi(url, buildUserDataObject);
};

const queryRrogatById = async function (nid, token = cognitoJwtToken) {
  if (!nid || !token) {
    error(`Invalid parameters: fname: ${nid}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.RROGAT_SEARCH}?gjc_nid=${nid}&cognito_token=${token}`;
  return callGjcApi(url, buildRrogatObject);
};

const queryPlateById = async function (nid, token = cognitoJwtToken) {
  if (!nid || !token) {
    error(`Invalid parameters: fname: ${nid}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.PLATE_SEARCH}?gjc_nid=${nid}&cognito_token=${token}`;
  return callGjcApi(url, buildPlateObject);
};

const queryCelById = async function (nid, token = cognitoJwtToken) {
  if (!nid || !token) {
    error(`Invalid parameters: fname: ${nid}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.CELL_SEARCH}?gjc_nid=${nid}&cognito_token=${token}`;
  return callGjcApi(url, buildCelObject);
};

const queryBottom100Names = async function (year, token = cognitoJwtToken) {
  if (!year || !token) {
    error(`Invalid parameters: fname: ${year}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.NAME_BOTTOM_100}?gjc_dob=${year}&cognito_token=${token}`;
  return callGjcApi(url, buildValueSasiaObject);
};

const queryByPlateId = async function (plateId, token = cognitoJwtToken) {
  if (!plateId || !token) {
    error(`Invalid parameters: plateId: ${plateId}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.PLATE_BY_PLATE_ID}?gjc_plate_number=${plateId}&cognito_token=${token}`;
  return callGjcApi(url, buildPlateObject);
};

const queryNameCount = async function (fname, token = cognitoJwtToken) {
  if (!fname || !token) {
    error(`Invalid parameters: fname: ${fname}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.NAME_COUNT}?gjc_first_name=${fname}&cognito_token=${token}`;
  return callGjcApi(url, buildValueSasiaObject);
};

const querySurameCount = async function (surname, token = cognitoJwtToken) {
  if (!surname || !token) {
    error(`Invalid parameters: surname: ${surname}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.SURNAME_COUNT}?gjc_last_name=${surname}&cognito_token=${token}`;
  return callGjcApi(url, buildValueSasiaObject);
};

const queryTop100NamesByYear = async function (year, token = cognitoJwtToken) {
  if (!year || !token) {
    error(`Invalid parameters: year: ${year}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.NAME_TOP_100}?gjc_dob=${year}&cognito_token=${token}`;
  return callGjcApi(url, buildValueSasiaObject);
};

const queryTop100SurnamesEver = async function (token = cognitoJwtToken) {
  if (!token) {
    error(`Invalid parameters: token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.SURNAME_TOP_100}?cognito_token=${token}`;
  return callGjcApi(url, buildValueSasiaObject);
};

const queryByCellNr = async function (cellNr, token = cognitoJwtToken) {
  if (!cellNr || !token) {
    error(`Invalid parameters: cellNr: ${cellNr}, token: ${token}`);
    return null;
  }
  const url = `${baseUrl}${API.ENDPOINTS.CELL_BY_NUMBER}?gjc_tel_number=${cellNr}&cognito_token=${token}`;
  return callGjcApi(url, buildCelObject);
};

export {
  queryByFirstLastName,
  queryRrogatById,
  queryPlateById,
  queryCelById,
  queryGjcById,
  queryGjcByFamilyId,
  queryBottom100Names,
  queryByPlateId,
  queryNameCount,
  queryTop100NamesByYear,
  queryByCellNr,
  querySurameCount,
  queryTop100SurnamesEver,
  setCognitoJwtToken,
};
